/**
 * The feed: story cards, the flagged one-liner, and the thread timeline
 * (NEWS-297; kerf UI, NM-MACR4Q).
 *
 * Extracted by view rather than by kind, so a ticket about the feed lands
 * here and nowhere else. **Rendering only.** Every gesture on a card is handled
 * by one `delegate()` in `app.tsx`, matching the `data-*` attributes written
 * here — splitting views must not become splitting handlers (NEWS-126).
 *
 * The feed is composed from kerf UI components:
 *
 * - each day is a `ListHeader` above a `Grid` of cards, which reflows into as
 *   many columns as the work area has room for (NEWS-64/96);
 * - a story is a framed `ContentItem` (the component owns the surface, border,
 *   inset and radius); its sources are `ListItemLink`s; its actions are a
 *   compact `ToolbarControlGroup` (Save, Share) plus a `PopupMenu` whose items
 *   also open at the pointer on a right-click (`openPopupMenuAt` in `app.tsx`);
 * - the "story so far" expander is a `ListHeader` disclosure controlling the
 *   always-present pane below it (`aria-controls` must point at something,
 *   docs/3-ui.md, NEWS-99).
 *
 * Stories are addressed by `data-story-id` — not `data-item-id`, which kerf
 * UI's own controls (`Chip`, `ListItem`) write for their delegated actions.
 */

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Chip } from '@kerfjs/ui/chip';
import { ContentItem } from '@kerfjs/ui/content-item';
import { fillColor, px, uiColor } from '@kerfjs/ui/css-values';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetText } from '@kerfjs/ui/list-inset-text';
import { ListItem, ListItemLink } from '@kerfjs/ui/list-item';
import { PopupMenu, type PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import { Row } from '@kerfjs/ui/row';
import { Spacer } from '@kerfjs/ui/spacer';
import { Text } from '@kerfjs/ui/text';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import type { SafeHtml } from 'kerfjs';

import type { ThreadSummary } from '../../../api/schemas.js';
import type { NewsItem } from '../../../db/schemas.js';
import { cachedImageUrl } from '../../../image-url.js';
import { safeHref } from '../../../url-safety.js';
import type { ThreadPane } from '../../app/stores.js';
import { icon } from '../../components/icons.js';
import { dayKeyOf, dayLabel, relativeTime } from '../../lib/dates.js';
import { outletFor, publishedLabel } from './attribution.js';
import {
  showAllLabel,
  threadBadge,
  threadBadgeLabel,
  threadFetchNeeded,
  threadRowDate,
  visibleThreadRows,
} from './thread-view.js';

/** A story card's actions (NEWS-61): bookmark and share, then the off-topic flag. */
function storyMenuItems(item: NewsItem): PopupMenuEntry[] {
  const attrs = (action: string): { 'data-item-menu-action': string; 'data-story-ref': string } => ({
    'data-item-menu-action': action,
    'data-story-ref': item.id,
  });
  const flag: PopupMenuEntry = {
    label: item.offTopic ? 'Unflag Off Topic' : 'Flag as Off Topic',
    action: 'story-menu',
    icon: icon('flag'),
    attributes: attrs('flag'),
  };
  if (item.offTopic) return [flag];
  return [
    {
      label: item.saved ? 'Remove Bookmark' : 'Bookmark',
      action: 'story-menu',
      icon: icon('bookmark'),
      attributes: attrs('bookmark'),
    },
    { label: 'Share', action: 'story-menu', icon: icon('share'), attributes: attrs('share') },
    { kind: 'divider' },
    flag,
  ];
}

/**
 * The flagged one-liner (NEWS-61): a story marked off topic this session,
 * collapsed so a misclick stays visible and undoable until reload. Its "Off
 * Topic" chip is the unflag control.
 */
function flaggedRowJsx(item: NewsItem, topicName: string): SafeHtml {
  return (
    <ContentItem
      appearance="warning"
      frame="framed"
      rootAttributes={{ 'data-key': `flag-${item.id}`, 'data-story-id': item.id, 'data-flagged': 'true' }}
    >
      <List gap="2xs">
        {/* Topic + chip on one line, the title below — a long topic name next to
            the title read as cramped (NEWS-71). */}
        <Row gap="xs" wrap vAlign="middle">
          <Badge size="compact" maxWidth={px(220)}>
            {topicName}
          </Badge>
          <Chip
            action="unflag-story"
            itemId={item.id}
            size="compact"
            icon={icon('flag')}
            actionLabel="Off topic — unflag"
          >
            Off Topic
          </Chip>
          <Spacer flex />
          {storyMenuJsx(item)}
        </Row>
        <Text size="compact" data-flagged-title="">
          {item.title}
        </Text>
      </List>
    </ContentItem>
  );
}

/** The story's "…" menu, also opened at the pointer on a right-click. */
function storyMenuJsx(item: NewsItem): SafeHtml {
  return (
    <ToolbarControlGroup single nestedDropdown appearance="borderless" size="compact" menuInset="compact">
      <PopupMenu
        label={`More Actions for ${item.title}`}
        icon={icon('more')}
        caret={false}
        placement="bottom-end"
        items={storyMenuItems(item)}
        rootAttributes={{ 'data-story-menu': item.id }}
      />
    </ToolbarControlGroup>
  );
}

export interface FeedThreads {
  summaries: Record<string, ThreadSummary | undefined>;
  panes: Record<string, ThreadPane | undefined>;
  showAll: boolean;
}

const NO_THREADS: FeedThreads = { summaries: {}, panes: {}, showAll: false };

/**
 * One row of the timeline (NEWS-282): when it was found, what happened, and
 * the outlet, each linked to its source — except the story already on screen,
 * which says so instead of linking to itself.
 */
function threadRowJsx(entry: NewsItem, current: boolean): SafeHtml {
  const source = entry.sources.at(0) ?? null;
  const outlet = source === null ? '' : outletFor(source);
  const description = [threadRowDate(entry.foundAt), current ? 'this story' : outlet]
    .filter((part) => part !== '')
    .join(' · ');
  const attrs = {
    'data-key': `thread-${entry.id}`,
    'data-thread-row': entry.id,
    'data-current': current ? 'true' : undefined,
  };
  const href = source === null ? undefined : safeHref(source.url);
  if (current || href === undefined) {
    return (
      <ListItem
        action="thread-current"
        label={entry.title}
        description={description}
        density="compact"
        multiline
        selected={current}
        disabled={!current}
        rootAttributes={attrs}
      />
    );
  }
  return (
    <ListItemLink
      href={href}
      external
      label={entry.title}
      description={description}
      density="compact"
      multiline
      rootAttributes={attrs}
    />
  );
}

/**
 * The detail pane's content (NEWS-281): the thread timeline (NEWS-282), or
 * the state that stands in for it while it loads or after it fails.
 */
function threadPaneJsx(item: NewsItem, threads: FeedThreads): SafeHtml {
  const summary = threads.summaries[item.id];
  if (!threadFetchNeeded(summary)) {
    return (
      <ListInsetText>
        <Text tone="quiet" size="compact">
          No related stories yet — this is the only one on this subject so far.
        </Text>
      </ListInsetText>
    );
  }
  const pane = threads.panes[item.id];
  if (pane === undefined || pane.status === 'loading') {
    return (
      <ListInsetText>
        <Text tone="quiet" size="compact">
          Looking up the story so far…
        </Text>
      </ListInsetText>
    );
  }
  if (pane.status === 'error') {
    return (
      <Row gap="xs" wrap vAlign="middle" textInsets="l" rootAttributes={{ 'data-thread-error': '' }}>
        <Text variant="span" tone="danger" size="compact">
          Couldn't load the story so far: {pane.message}
        </Text>
        <Button size="compact" emphasis="plain" rootAttributes={{ 'data-retry-thread': item.id }}>
          Try Again
        </Button>
      </Row>
    );
  }
  const { rows, hidden } = visibleThreadRows(pane.items, threads.showAll);
  return (
    <List gap="2xs" rootAttributes={{ 'data-thread': '' }}>
      <ListHeader label="The Story So Far" size="xs" headingLevel={4} />
      {/* `.map()`, not `each()`: one pane is open at a time and its rows are
          fixed while it is, so memoizing them buys nothing (docs/3-ui.md). */}
      {rows.map((entry) => threadRowJsx(entry, entry.id === item.id))}
      {hidden > 0 ? (
        <Row controlInsets="l">
          <Button size="compact" emphasis="plain" action="show-all-thread">
            {showAllLabel(pane.items.length)}
          </Button>
        </Row>
      ) : null}
    </List>
  );
}

/** One source link: the outlet's favicon where we have it (NEWS-169), else an arrow. */
function sourceJsx(item: NewsItem, source: NewsItem['sources'][number], index: number): SafeHtml {
  const date = source.publishedAt !== null ? publishedLabel(source.publishedAt, item.foundAt) : '';
  const props = {
    density: 'compact' as const,
    multiline: true,
    icon:
      source.favicon !== null ? (
        <img class="favicon" src={cachedImageUrl(source.favicon.hash)} alt="" width="16" height="16" />
      ) : (
        icon('arrow')
      ),
    label: source.title !== '' ? source.title : source.url,
    description: [outletFor(source), date].filter((part) => part !== '').join(' · '),
    rootAttributes: { 'data-key': `${item.id}-${String(index)}`, 'data-source': '' },
  };
  // A source whose URL is not http(s) (NM-122) is shown but never linked.
  const href = safeHref(source.url);
  return href === undefined ? (
    <ListItem action="unsafe-source" disabled {...props} />
  ) : (
    <ListItemLink href={href} external {...props} />
  );
}

function itemJsx(
  item: NewsItem,
  topicName: string,
  variant: 'normal' | 'review' = 'normal',
  expandedItemId: string | null = null,
  threads: FeedThreads = NO_THREADS,
  scheme: 'light' | 'dark' = 'light',
): SafeHtml {
  if (variant === 'normal' && item.offTopic) return flaggedRowJsx(item, topicName);
  const review = variant === 'review';
  const paneId = `item-pane-${item.id}`;
  const expanded = !review && expandedItemId === item.id;
  const threadSummary = threads.summaries[item.id];
  const badge = threadBadge(threadSummary);
  const imageColor = item.image?.color;
  return (
    <ContentItem
      frame="framed"
      appearance="surface"
      media={
        item.image === null
          ? undefined
          : {
              src: cachedImageUrl(item.image.hash),
              alt: '',
              aspectRatio: 16 / 9,
              deferOffscreen: true,
              parallax: 'subtle',
              blur: { radius: px(16) },
              fadeTo: uiColor('surface'),
            }
      }
      tint={
        imageColor === undefined || imageColor === null
          ? undefined
          : {
              color: fillColor(imageColor),
              accent: fillColor(imageColor),
              scheme,
            }
      }
      rootAttributes={{
        'data-key': item.id,
        'data-story-id': item.id,
        'data-saved': item.saved ? 'true' : undefined,
        'data-expanded': expanded ? 'true' : undefined,
      }}
    >
      <List gap="xs">
        <Row gap="xs" vAlign="middle">
          <Badge size="compact" tone="info" maxWidth={px(240)} rootAttributes={{ 'data-story-topic': '' }}>
            {topicName}
          </Badge>
          <Text variant="span" tone="quiet" size="compact">
            {relativeTime(item.foundAt)}
          </Text>
          <Spacer flex />
          {review
            ? // Review mode: marked, and its menu is where it is unflagged (NEWS-61).
              [
                <Badge
                  size="compact"
                  tone="warning"
                  icon={icon('flag')}
                  rootAttributes={{ 'data-off-topic-label': '' }}
                >
                  Off Topic
                </Badge>,
                storyMenuJsx(item),
              ]
            : [
                <ToolbarControlGroup
                  label="Story Actions"
                  appearance="borderless"
                  size="compact"
                  buttonAppearance="push"
                >
                  <button
                    type="button"
                    data-save-item={item.id}
                    data-saved={item.saved ? 'true' : 'false'}
                    aria-pressed={item.saved ? 'true' : 'false'}
                    aria-label="Save Story"
                  >
                    {icon('bookmark')}
                  </button>
                  <button type="button" data-share-item={item.id} aria-label="Share Story">
                    {icon('share')}
                  </button>
                </ToolbarControlGroup>,
                storyMenuJsx(item),
              ]}
        </Row>
        <Text variant="h3" size="large" flush>
          {item.title}
        </Text>
        <Text flush>{item.summary}</Text>
        {/* `.map()`, not `each()`: sources are static per item (docs/3-ui.md). */}
        <List gap="2xs" rootAttributes={{ 'data-sources': '' }}>
          {item.sources.map((source, i) => sourceJsx(item, source, i))}
        </List>
        {/* The thread expander (NEWS-281, NM-121): only when the thread holds more
            than this story, so a thread-of-one — the common case — gets no control. */}
        {badge === null || review ? null : (
          <ListHeader
            toggle
            size="xs"
            action="expand-story"
            expanded={expanded}
            label={expanded ? 'Hide Story So Far' : `Story So Far · ${badge.count}`}
            triggerAttributes={{ 'data-expand-item': item.id, 'aria-controls': paneId }}
            rootAttributes={{ 'data-thread-expand': threadBadgeLabel(threadSummary) }}
          />
        )}
        {/* Always-present detail pane: the target of the expander's `aria-controls`
            (an `aria-controls` pointing at nothing is an axe violation). */}
        <div id={paneId} data-story-pane>
          {expanded ? threadPaneJsx(item, threads) : ''}
        </div>
      </List>
    </ContentItem>
  );
}

/**
 * The narrowest a story card gets, px. With `autoFill` it also sets the
 * widest: a track only grows until a further one fits, so under twice this.
 */
export const CARD_MIN_WIDTH_PX = 340;

/**
 * The feed's day groups: one `ListHeader` per local day (NEWS-282's one date
 * format), its stories in a reflowing `Grid`.
 */
export function feedJsx(
  items: NewsItem[],
  topicNames: Map<string, string>,
  variant: 'normal' | 'review' = 'normal',
  expandedItemId: string | null = null,
  threads: FeedThreads = NO_THREADS,
  scheme: 'light' | 'dark' = 'light',
): SafeHtml[] {
  const groups = new Map<string, NewsItem[]>();
  for (const item of items) {
    const key = dayKeyOf(new Date(item.foundAt));
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()].map(([dateKey, dayItems]) => (
    <section data-key={`day-${dateKey}`} data-day={dateKey}>
      <ListHeader label={dayLabel(dateKey)} size="xs" headingLevel={2} />
      {/* Cards are bounded both ways (NM-VXFQBD): never narrower than the
          340px minimum, and — with `autoFill` keeping the empty tracks of a
          sparse day rather than stretching its few cards across them — never
          as wide as two columns. A day with one story used to get one card
          the whole feed wide. */}
      <Grid minColumnWidth={px(CARD_MIN_WIDTH_PX)} autoFill gap="xs">
        {dayItems.map((item) =>
          itemJsx(item, topicNames.get(item.topicId) ?? 'unknown topic', variant, expandedItemId, threads, scheme),
        )}
      </Grid>
    </section>
  ));
}
